import React from 'react';

export enum FilterPlacement {
  Chiclet = 'chiclet',
  Menu = 'menu',
}

export enum FilterRenderMode {
  Chiclet = 'chiclet',
  Inline = 'inline',
}

export interface SelectFilterOption {
  /** The value passed to onChange when this option is selected. */
  value: string;
  /** The label displayed in the filter list. */
  text: string;
  /** Whether this option is selected by default on initial render. */
  selected?: boolean;
}

interface BaseFilterConfig {
  /** Unique identifier for the filter. Used as the key for state tracking. */
  id: string;
  /** Display name shown on the chiclet button and inside the filter popover. */
  name: string;
  /** Optional icon displayed on the chiclet button. */
  icon?: React.ReactNode;
  /** Whether the filter starts in the chiclet bar or inside the More Filters menu. */
  placement: FilterPlacement;
  /** Whether this filter is rendered at all. Defaults to false if omitted. */
  defaultVisible?: boolean;
}

export interface SelectFilterConfig extends BaseFilterConfig {
  type: 'select';
  /** When false, selecting an option replaces the current selection instead of toggling it. Defaults to true. */
  multiSelect?: boolean;
  options: SelectFilterOption[];
  /** Called with the full array of currently selected values after each change. */
  onChange: (values: string[]) => void;
}

export interface DateFilterConfig extends BaseFilterConfig {
  type: 'date';
  selected?: Date | null;
  /** Called with the selected date, or null when the filter is cleared. */
  onChange: (date: Date | null) => void;
}

export interface DateRangeFilterConfig extends BaseFilterConfig {
  type: 'dateRange';
  startDate?: Date | null;
  endDate?: Date | null;
  native?: boolean;
  /** Called with a [startDate, endDate] tuple. Either value may be null if not yet selected. */
  onChange: (range: [Date | null, Date | null]) => void;
}

export interface SearchFilterConfig extends BaseFilterConfig {
  type: 'search';
  searchTerm?: string | null;
  /** How long to wait after the last keystroke before calling onChange. Defaults to 1000ms. */
  debounceMs?: number;
  /** Called with the current search term, or null when the input is cleared. */
  onChange: (term: string | null) => void;
}

export interface CustomFilterConfig extends BaseFilterConfig {
  type: 'custom';
  /** Render function called with the current render mode so the filter can adapt its layout. */
  render: (mode: FilterRenderMode) => React.ReactNode;
}

export type FilterConfig =
  | SelectFilterConfig
  | DateFilterConfig
  | DateRangeFilterConfig
  | SearchFilterConfig
  | CustomFilterConfig;
